import { useEffect, useMemo, useState } from 'react'
import { Button, TextArea } from '@heroui/react'
import { api } from '../lib/api'
import { setWording, shippedWord } from '../lib/i18n/catalog'
import { sourcePhrases } from '../lib/i18n/english'
import { LANGUAGES, SOURCE_LANGUAGE } from '../lib/i18n/languages'
import { useWords } from '../lib/i18n/useWords'
import { explain } from '../lib/staffErrors'
import type { WordingView } from '../lib/types'
import { findRows, optionalPlaceholders, screenRows, withSaved, withoutWord, wordingMap, wordingProblem, wordingRows, type WordingProblem, type WordingRow } from '../lib/wording'
import { DESIGN_WORD_NAMES } from '../layout/words'
import { useSession } from '../ui/Session'
import { useStaffText } from '../ui/StaffText'
import { useFlash } from './sectionKit'
import { ChoiceSelect, InnerPage, ListCard, ListRow, ListTable, Notice, PageHead, RowArrow, useOpenRecord, Values } from './settingsKit'
import '../styles/connect.css'
import '../styles/wording.css'

/** Rows drawn at once; the rest wait for a narrower search. */
const SHOWN = 200

type Say = ReturnType<typeof useStaffText>['say']

function problemText(p: WordingProblem, say: Say): string {
  switch (p.kind) {
    case 'empty':
      return say('wording.problem.empty')
    case 'long':
      return say('wording.problem.long', { max: p.max })
    case 'markup':
      return say('wording.problem.markup')
    case 'placeholders':
      return p.need.length
        ? say('wording.problem.placeholders', { names: p.need.map((n) => `{${n}}`).join(' ') })
        : say('wording.problem.noPlaceholders')
    case 'onlyPlaceholders':
      return say('wording.problem.onlyPlaceholders', { names: braced(p.may) })
  }
}

const braced = (names: readonly string[]) => names.map((n) => `{${n}}`).join(' ')

/** A screen word's name where its English is placeholders alone ("{desk}" is the Desk label). */
function rowTitle(row: WordingRow, say: Say): string {
  const name = DESIGN_WORD_NAMES[row.key.replace(/^kiosk\./, '')]
  return name && row.key.startsWith('kiosk.') && !/\p{L}/u.test(row.en.replace(/\{[^}]*\}/g, '')) ? say(name) : row.en
}

/** Settings › Organisation › Wording (#wording): the organisation's word for any platform phrase. */
export function Wording({ organisation }: { organisation?: string }) {
  const { lang, say } = useStaffText()
  const session = useSession()
  const { flash, toast } = useFlash()
  const [view, setView] = useState<WordingView | null>(null)
  const [loadError, setLoadError] = useState('')
  const [target, setTarget] = useState(SOURCE_LANGUAGE)
  const [q, setQ] = useState('')
  const [filter, setFilter] = useState<'all' | 'changed' | 'screens'>('all')
  const version = useWords(target)

  useEffect(() => {
    setView(null)
    setLoadError('')
    api
      .wording(organisation)
      .then(setView)
      .catch((err) => setLoadError(explain(err, lang, 'wording.notLoaded')))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [organisation])

  const rows = useMemo(
    () => (view ? wordingRows(sourcePhrases(), view.server, view.overrides, target, shippedWord) : []),
    // version: a language file arrived.
    [view, target, version],
  )
  const screens = useMemo(() => screenRows(rows), [rows])
  const found = useMemo(() => findRows(filter === 'screens' ? screens : rows, q, filter === 'changed'), [rows, screens, q, filter])
  const changedCount = rows.filter((r) => r.own !== undefined).length
  const page = useOpenRecord(rows, (r) => r.key, view !== null)

  // The signed-in person's own organisation sees its words at once; the owner's view of another does not.
  const own = Boolean(view && session && view.organisation === session.identity.staff.tenantId)
  const apply = (overrides: WordingView['overrides']) => {
    setView((v) => (v ? { ...v, overrides } : v))
    if (own) setWording(wordingMap(overrides))
  }

  if (view && page.row) {
    return (
      <>
        {toast}
        <WordPage
          key={`${target}:${page.row.key}`}
          row={page.row}
          lang={target}
          organisation={organisation}
          maxLength={view.maxLength}
          onClose={page.close}
          onSaved={(saved) => {
            apply(withSaved(view.overrides, saved))
            page.close()
            flash(say('wording.saved'))
          }}
          onReset={(key) => {
            apply(withoutWord(view.overrides, key, target))
            page.close()
            flash(say('wording.wasReset'))
          }}
        />
      </>
    )
  }

  const showsPlatform = target !== SOURCE_LANGUAGE
  const head = [say('wording.col.english'), ...(showsPlatform ? [say('wording.col.platform')] : []), say('wording.col.yours'), '']
  const cols = showsPlatform ? 'minmax(0, 1fr) minmax(0, 1fr) minmax(0, 1fr) 40px' : 'minmax(0, 1fr) minmax(0, 1fr) 40px'
  const more = found.length > SHOWN

  return (
    <>
      <PageHead
        title={say('wording.section')}
        line={say('wording.line')}
        actions={
          <span className="wd-lang">
            <span aria-hidden="true">{say('wording.language')}</span>
            <ChoiceSelect
              label={say('wording.language')}
              value={target}
              onChange={setTarget}
              options={LANGUAGES.map((l) => ({ value: l.code, label: l.name }))}
            />
          </span>
        }
      />
      {toast}
      {loadError && <Notice className="wd-gap">{loadError}</Notice>}

      {view && (
        <ListCard
          title={say('wording.list')}
          count={say('wording.count', { n: found.length })}
          search={q}
          onSearch={setQ}
          searchLabel={say('wording.find')}
          chipGroups={[
            {
              label: say('wording.filter'),
              chip: filter,
              onChip: (v) => setFilter(v as 'all' | 'changed' | 'screens'),
              chips: [
                { value: 'all', label: say('wording.all'), count: rows.length },
                { value: 'changed', label: say('wording.changed'), count: changedCount },
                { value: 'screens', label: say('wording.screens'), count: screens.length },
              ],
            },
          ]}
          foot={more ? <p className="wd-more">{say('wording.more', { shown: SHOWN, n: found.length })}</p> : undefined}
        >
          {found.length === 0 ? (
            <p className="empty">{say('wording.none')}</p>
          ) : (
            <ListTable label={say('wording.list')} head={head} cols={cols}>
              {found.slice(0, SHOWN).map((row) => (
                <ListRow key={`${target}:${row.key}`} label={rowTitle(row, say)} onOpen={() => page.open(row)}>
                  <span role="cell" className="wd-text" title={row.key}>
                    {rowTitle(row, say)}
                  </span>
                  {showsPlatform && (
                    <span role="cell" className="wd-text">
                      {row.platform}
                    </span>
                  )}
                  <span role="cell" className="wd-text" data-own={row.own !== undefined ? 'yes' : 'no'}>
                    {row.own ?? '—'}
                  </span>
                  <RowArrow />
                </ListRow>
              ))}
            </ListTable>
          )}
        </ListCard>
      )}
    </>
  )
}

/** One phrase's own page: the organisation's word in the chosen language, and Reset. */
function WordPage({
  row,
  lang,
  organisation,
  maxLength,
  onClose,
  onSaved,
  onReset,
}: {
  row: WordingRow
  lang: string
  organisation?: string
  maxLength: number
  onClose: () => void
  onSaved: (saved: WordingView['overrides'][number]) => void
  onReset: (key: string) => void
}) {
  const { lang: reader, say } = useStaffText()
  const [draft, setDraft] = useState(row.own ?? row.platform)
  const [busy, setBusy] = useState(false)
  const [refusal, setRefusal] = useState('')
  const optional = optionalPlaceholders(row.key)
  const problem = wordingProblem(draft, row.en, maxLength, optional)

  const run = async (act: () => Promise<void>) => {
    if (busy) return
    setBusy(true)
    setRefusal('')
    try {
      await act()
    } catch (err) {
      setRefusal(explain(err, reader, 'common.unreachable'))
    } finally {
      setBusy(false)
    }
  }
  const save = () => run(async () => onSaved(await api.saveWording({ key: row.key, lang, text: draft.trim() }, organisation)))
  const reset = () =>
    run(async () => {
      await api.resetWording(row.key, lang, organisation)
      onReset(row.key)
    })
  const language = LANGUAGES.find((l) => l.code === lang)?.name ?? lang

  return (
    <InnerPage
      back={say('wording.section')}
      title={rowTitle(row, say)}
      line={language}
      onClose={onClose}
      onSubmit={() => void save()}
      valid={!problem}
      busy={busy}
      error={refusal || undefined}
      submitLabel={say('wording.save')}
      busyLabel={say('wording.saving')}
      extra={
        row.own !== undefined ? (
          <Button variant="ghost" isDisabled={busy} onPress={() => void reset()}>
            {say('wording.reset')}
          </Button>
        ) : undefined
      }
    >
      {lang !== SOURCE_LANGUAGE && <Values items={[{ label: say('wording.col.platform'), value: row.platform }]} />}
      <label className="field">
        <span>{say('wording.editLabel')}</span>
        <TextArea fullWidth className="connect-textarea" rows={3} value={draft} autoFocus aria-invalid={!!problem} onChange={(e) => setDraft(e.target.value)} />
      </label>
      {problem && <p className="field-error">{problemText(problem, say)}</p>}
      {optional && !problem && <p className="wd-hint">{say('wording.mayUse', { names: braced(optional) })}</p>}
    </InnerPage>
  )
}
